// src/render/audio/constants.ts
// All Phase 6 audio + bubble + fear-feedback tunables. Values are render-side
// ms / px / multiplier units (NOT sim ticks). TODO(P7 POL-06): retune from
// playtest. Source: 06-CONTEXT.md D-02..D-23, 06-UI-SPEC.md.
//
// Conventions:
//   - All constants are P6_*-prefixed so a single grep surfaces every retunable.
//   - Time units are milliseconds; spatial units are CSS pixels or tiles
//     (commented per-constant). NEVER tick-based — P6 lives outside the sim
//     determinism contract (CONTEXT "30 Hz sim, variable render").
//   - Stinger pool sizes are floors enforced by Howler `pool` option (D-20).

// ---------------------------------------------------------------------------
// Bubble layer (D-02, UI-SPEC §Bubble + §BubbleLayer)
// ---------------------------------------------------------------------------

/** D-02: maximum concurrent bubbles before tier preempt kicks in. */
export const P6_BUBBLE_CAP = 3;

/** UI-SPEC §Typography: typewriter cadence (chars/sec). */
export const P6_BUBBLE_TYPEWRITER_CPS = 28;

/** UI-SPEC §Bubble: pause appended after sentence-end punctuation (.?!). */
export const P6_BUBBLE_TYPEWRITER_PUNCT_PAUSE_MS = 120;

/** UI-SPEC §Bubble: pause appended after a comma. */
export const P6_BUBBLE_TYPEWRITER_COMMA_PAUSE_MS = 40;

/** D-05 / DIAL-07: bubble lifetime floor in milliseconds. */
export const P6_BUBBLE_LIFETIME_MIN_MS = 1500;

/** D-05 / DIAL-07: extra time appended after waffle audio finishes. */
export const P6_BUBBLE_LIFETIME_PADDING_MS = 500;

/** UI-SPEC §Bubble: opacity 0→1 fade-in duration on bubble mount. */
export const P6_BUBBLE_FADE_IN_MS = 150;

/** UI-SPEC §Bubble: lifetime-expire fade-out duration. */
export const P6_BUBBLE_FADE_OUT_MS = 300;

/** UI-SPEC §Bubble: fade duration when a higher-tier bubble preempts a lower-tier one. */
export const P6_BUBBLE_PREEMPT_FADE_MS = 200;

/** UI-SPEC §Wobble: one-shot wobble keyframe length on mount. */
export const P6_BUBBLE_WOBBLE_DURATION_MS = 600;

/** UI-SPEC §Bubble: viewport-edge threshold (px) for tail auto-flip. */
export const P6_BUBBLE_SAFE_AREA_FLIP_PX = 8;

/**
 * Plan 06-14 (UAT Gap 8 closure): per-entity replace-on-admit toggle.
 *
 * When `true`, BubbleLayer's admit() filters `prev` to remove any existing
 * bubble whose entityId matches the incoming entry's entityId BEFORE invoking
 * tryAdd. Net effect: each entity displays AT MOST ONE bubble at a time —
 * the round-start quote and the 'Liberdade!' free-all bubble for the SAME
 * speaker (the player, e0, in single-player) no longer overlap visually.
 *
 * The global cap-3 + tier preempt logic (D-02) is unaffected: per-entity
 * replace runs FIRST, then tryAdd applies the cross-entity rules to the
 * filtered list. Cross-entity bubbles still observe cap-3 + tier preempt.
 *
 * Kept as an explicit toggle (rather than hard-coded into BubbleLayer) so
 * P7 polish can A/B retune the bubble admission policy without re-deploying
 * — alternatives to consider include the debug session's "800 ms per-entity
 * cooldown" (more permissive) or a tier-aware variant that only replaces
 * when the incoming tier >= existing tier.
 *
 * TODO(P7 POL-06): retune from playtest. Current debug-session preference
 * is the simpler replace-on-admit rule (this default).
 */
export const P6_BUBBLE_PER_ENTITY_REPLACE = true;

// ---------------------------------------------------------------------------
// Camera bias (D-03)
// ---------------------------------------------------------------------------

/** D-03: player-bubble camera bias magnitude in screen pixels (32..48 midpoint). */
export const P6_CAMERA_BIAS_PX = 40;

/** D-03: lerp duration in render frames (~133ms @ 60fps). */
export const P6_CAMERA_BIAS_LERP_FRAMES = 8;

// ---------------------------------------------------------------------------
// Waffle voice (D-05, D-06, D-07)
// ---------------------------------------------------------------------------

/** D-05: target syllable cadence in syllables-per-second. */
export const P6_WAFFLE_SYLLABLES_PER_SEC = 8;

/** D-05: derived per-syllable interval in ms (= 1000 / SYLLABLES_PER_SEC). */
export const P6_WAFFLE_SYLLABLE_INTERVAL_MS = 125;

/** D-06: lower bound of per-syllable random pitch jitter (Howl.rate multiplier). */
export const P6_WAFFLE_PITCH_JITTER_MIN = 0.97;

/** D-06: upper bound of per-syllable random pitch jitter (Howl.rate multiplier). */
export const P6_WAFFLE_PITCH_JITTER_MAX = 1.03;

/**
 * D-07: shared adult-pitch override (single value across all adult NPCs).
 * Multiplied with the same waffle pack via Howl.rate; deeper than character
 * default to read as adult-on-the-sidelines vs the kids playing.
 */
export const P6_ADULT_VOICE_PITCH = 0.85;

// ---------------------------------------------------------------------------
// Footsteps (D-09..D-12)
// ---------------------------------------------------------------------------

/** D-12: minimum gap between footstep SFX per entity (ms). */
export const P6_FOOTSTEP_THROTTLE_MS = 250;

/** D-12: skip footstep playback if source is further than this (tiles) from local player. */
export const P6_FOOTSTEP_DISTANCE_CULL_TILES = 12;

/** D-12: sprint-state gain multiplier on top of base footstep volume. */
export const P6_FOOTSTEP_SPRINT_GAIN = 1.2;

/** D-10: how strongly `move.quietness` reduces footstep volume (0..1; 0=no effect, 1=full mute at quietness=1). */
export const P6_QUIETNESS_VOLUME_WEIGHT = 0.6;

/** D-10: base footstep Howl volume before quietness + distance modulation. */
export const P6_FOOTSTEP_BASE_VOLUME = 0.5;

// ---------------------------------------------------------------------------
// Fear feedback (D-13, D-14, UI-SPEC §FearFeedbackLayer)
// ---------------------------------------------------------------------------

/** D-13: heartbeat BPM at the `nervous` threshold (fear ≥ 0.35). */
export const P6_HEARTBEAT_BPM_NERVOUS = 50;

/** D-13: heartbeat BPM at the `afraid` threshold (fear ≥ 0.65). */
export const P6_HEARTBEAT_BPM_AFRAID = 80;

/** D-13: heartbeat BPM at the `terrified` threshold (fear ≥ 0.90). */
export const P6_HEARTBEAT_BPM_TERRIFIED = 100;

/**
 * D-13: BPM of the recorded heartbeat sample (P6-Pitfall F mid-range pick).
 * Howler `Howl.rate` is computed as `target_bpm / BASE_BPM` so the same
 * sample drives every threshold via rate modulation.
 */
export const P6_HEARTBEAT_BASE_BPM = 75;

/** D-13: vignette opacity hard cap at fear=1.0 (terrified). */
export const P6_VIGNETTE_PEAK_OPACITY = 0.35;

/** D-13: peak shiver amplitude (px) on the local player sprite at fear=1.0. */
export const P6_SHIVER_AMPLITUDE_PX = 2;

/** UI-SPEC §FearFeedbackLayer: shiver oscillation frequency (Hz). */
export const P6_SHIVER_HZ = 4;

/** P4 D-19/D-20 mirror: lower threshold for `nervous` band (vignette/shiver gate). */
export const P6_FEAR_THRESHOLD_NERVOUS = 0.35;

/** P4 D-19/D-20 mirror: lower threshold for `afraid` band. */
export const P6_FEAR_THRESHOLD_AFRAID = 0.65;

/** P4 D-19/D-20 mirror: lower threshold for `terrified` band. */
export const P6_FEAR_THRESHOLD_TERRIFIED = 0.9;

/** D-15: fade-out duration for FearFeedbackLayer when local player is caught. */
export const P6_FEAR_FADE_OUT_ON_CAUGHT_MS = 500;

/** Plan 06-11: shiver release envelope. After a fear impulse, sprite shiver
 *  decays to 0 over this many ms regardless of underlying fear value.
 *  TODO(P7 POL-06): retune from playtest if 2000 feels too snappy/sluggish. */
export const P6_SHIVER_DECAY_MS = 2000;

/** Plan 06-11: fear-impulse-up detection threshold. The shiver-decay envelope
 *  re-arms when (nextFear - lastFear) > this value. M7 (checker): pinned to
 *  delta-based heuristic so re-arm fires on rapid-onset catch events without
 *  spuriously re-arming on every steady-state ai-debug tick.
 *  TODO(P7 POL-06): retune if catch events feel under/over-emphasised. */
export const P6_FEAR_IMPULSE_DELTA = 0.1;

// ---------------------------------------------------------------------------
// Howler config (D-17, D-19, D-20)
// ---------------------------------------------------------------------------

/** D-17: master Howler volume multiplier (`Howler.volume(...)`). */
export const P6_AUDIO_VOLUME = 0.8;

// ---------------------------------------------------------------------------
// P7 UI-06 bus-split slider defaults (07-UI-SPEC §Surface 2 — LOCKED)
// ---------------------------------------------------------------------------

/** P7 UI-06: Master slider default (100%). Seeds Howler global master at init. */
export const P7_MASTER_DEFAULT = 1.0;

/** P7 UI-06: Music slider default (70%). Music bus = calm + wind-down loops. */
export const P7_BUS_MUSIC_DEFAULT = 0.7;

/** P7 UI-06: SFX slider default (100%). SFX bus = ambient/stingers/footsteps/waffle/heartbeat. */
export const P7_BUS_SFX_DEFAULT = 1.0;

/** D-20: Howler `pool` for the footstep Howl. */
export const P6_HOWL_POOL_FOOTSTEP = 4;

/** D-20: Howler `pool` for the waffle syllable atlas Howl. */
export const P6_HOWL_POOL_WAFFLE = 4;

/** D-20: Howler `pool` for the stinger Howls (one-shot eviction). */
export const P6_HOWL_POOL_STINGER = 4;

/** D-21: ambient night-bed fade-in on first arm. */
export const P6_AMBIENT_FADE_IN_MS = 1500;

/** D-22: music swap fade duration on round-started/round-ended transitions. */
export const P6_MUSIC_FADE_MS = 800;

/** D-22: music loop volume (kept below ambient so dialogue waffle reads cleanly). */
export const P6_MUSIC_VOLUME = 0.4;

/** D-21: ambient night-bed loop volume. */
export const P6_AMBIENT_VOLUME = 0.5;

// ---------------------------------------------------------------------------
// iOS unlock probe (D-18)
// ---------------------------------------------------------------------------

/**
 * D-18: ms to wait after boot before probing `Howler.ctx.state` to decide
 * whether the returning-session AudioUnlockSplash needs to render. Long
 * enough for Howler to lazy-init the AudioContext on Android Chrome, short
 * enough to feel instant on returning-session entry.
 */
export const P6_UNLOCK_BOOT_PROBE_MS = 100;
